// How a widget's insides fill its tile, whatever size someone drags it to.
//
// A widget's content is measured in em: its natural size is how many ems
// wide and tall it is. Its type size is then the one at which that content
// fills the tile's inside, the way a picture set to "contain" fills a frame,
// and everything inside, set in em, grows and shrinks with it: type, icons,
// buttons and gaps. A widget that can lay the same things out in several
// ways (side by side, in rows, in a grid) gets the one that lets them be
// biggest, and one with less to show at small sizes says what it drops.
// Pure, so tests can check every size without a browser.

export interface Box {
  w: number;
  h: number;
}

export interface Layout<T extends string = string> {
  id: T;
  /** the content's size, in em */
  natural: Box;
  /** the smallest type size, in px, at which this layout still reads; below it the next one is tried */
  min?: number;
}

/** The type size, in px, at which content of natural size (in em) fills box: as big as fits, never past max. */
export function fitType(box: Box, natural: Box, max = Infinity): number {
  if (!(box.w > 0 && box.h > 0 && natural.w > 0 && natural.h > 0)) return 0;
  return Math.min(max, box.w / natural.w, box.h / natural.h);
}

/**
 * The first layout, in the order given (richest first), that still reads at
 * the size it fits; the last one when none does. Each comes with its type size.
 */
export function pickLayout<T extends string>(box: Box, layouts: Layout<T>[], max = Infinity): { id: T; px: number } {
  let last = { id: layouts[layouts.length - 1].id, px: 0 };
  for (const l of layouts) {
    const px = fitType(box, l.natural, max);
    last = { id: l.id, px };
    if (px >= (l.min ?? 0)) return last;
  }
  return last;
}

/** Of layouts that show the same things, the one whose content can be drawn biggest; ties go to the earlier. */
export function biggest<T extends string>(box: Box, layouts: Layout<T>[], max = Infinity): { id: T; px: number } {
  let best = { id: layouts[0].id, px: -1 };
  for (const l of layouts) {
    const px = fitType(box, l.natural, max);
    if (px > best.px + 1e-9) best = { id: l.id, px };
  }
  return best;
}

/** n cells of one size in a grid of cols columns, with a gap between them: the grid's natural size. */
export function gridBox(n: number, cols: number, cell: Box, gap: number): Box {
  const c = Math.max(1, Math.min(cols, n));
  const rows = Math.max(1, Math.ceil(n / c));
  return { w: c * cell.w + (c - 1) * gap, h: rows * cell.h + (rows - 1) * gap };
}

/** A type size rounded to a quarter pixel, so a drag that changes nothing visible does not draw again. */
export const quarter = (px: number): number => Math.round(px * 4) / 4;
